import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { coworkingAPI } from '../lib/api';
import { Building, MapPin, Globe, Check } from 'lucide-react';

export const CoworkingPage = () => {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const res = await coworkingAPI.getAll();
        setSpaces(res.data);
      } catch (err) {
        console.error('Error fetching coworking spaces:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSpaces();
  }, []);

  const defaultSpaces = [
    {
      id: '1',
      name: 'Bridgespace',
      description: 'Portland\'s premier tech-focused coworking space with modern amenities and a vibrant community.',
      address: '1000 SW Broadway, Portland, OR',
      website: 'https://bridgespace.io',
      amenities: ['High-speed WiFi', 'Conference rooms', 'Event space', 'Kitchen', 'Bike storage'],
      price_range: '$300-500/month',
    },
    {
      id: '2',
      name: 'WeWork Pioneer Place',
      description: 'Flexible workspace in the heart of downtown Portland with professional amenities.',
      address: '700 SW 5th Ave, Portland, OR',
      website: 'https://wework.com',
      amenities: ['24/7 access', 'Phone booths', 'Coffee bar', 'Wellness room'],
      price_range: '$400-800/month',
    },
    {
      id: '3',
      name: 'CENTRL Office',
      description: 'Creative workspace designed for startups and growing teams in Portland.',
      address: '1355 NW Everett St, Portland, OR',
      website: 'https://centrloffice.com',
      amenities: ['Private offices', 'Hot desks', 'Meeting rooms', 'Rooftop'],
      price_range: '$250-600/month',
    },
  ];

  const displaySpaces = spaces.length > 0 ? spaces : defaultSpaces;

  return (
    <div data-testid="coworking-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Spaces</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Coworking & Spaces</h1>
          <p className="text-muted-foreground max-w-2xl">
            Find the perfect workspace for your team. Portland offers a variety of 
            coworking spaces, innovation hubs, and creative studios.
          </p>
        </div>
      </section>

      {/* Spaces List */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-sm mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displaySpaces.map((space, index) => (
                <Card 
                  key={space.id}
                  className="card-hover h-full animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`coworking-${space.id}`}
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-sm bg-muted flex items-center justify-center mb-6">
                      <Building className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-xl mb-2">{space.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {space.description}
                    </p>
                    <div className="space-y-2 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {space.address}
                      </div>
                      {space.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          <a 
                            href={space.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary transition-colors"
                          >
                            Visit website
                          </a>
                        </div>
                      )}
                    </div>
                    {space.amenities && space.amenities.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                          Amenities
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {space.amenities.slice(0, 4).map((amenity, i) => (
                            <span 
                              key={i}
                              className="text-xs bg-muted px-2 py-1 rounded-full"
                            >
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {space.price_range && (
                      <div className="border-t border-border mt-4 pt-4">
                        <span className="text-sm font-medium">{space.price_range}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
